import { isArray } from 'lodash';
import { useCallback } from 'react';
import { URLSearchParamsInit, useSearchParams } from 'react-router-dom';

interface UseFilterParamsProps {
  filter?: URLSearchParamsInit;
}

interface ParamsFunctions<T> {
  filterParams: URLSearchParams;
  setFilterParams: <K = T>(params: Partial<K>) => void;
  deleteFilterParams: <T, P = T>(
    keys: keyof T | (keyof T)[],
    params?: Partial<P>,
  ) => void;
  deleteFilterValue: <T, P = T>(
    key: keyof T,
    value?: string,
    params?: Partial<P>,
  ) => void;
}

function applyParams<T>(searchParams: URLSearchParams, params: Partial<T>) {
  for (const key in params) {
    const value = params[key];
    searchParams.delete(key);
    if (!value) continue;
    if (isArray(value))
      value.forEach(value => searchParams.append(key, value as string));
    else searchParams.append(key, value as string);
  }
}

export default function useFilterParams<K>({
  filter,
}: UseFilterParamsProps = {}): ParamsFunctions<K> {
  const [filterParams, setSearchParams] = useSearchParams(filter);

  const setFilterParams = useCallback(
    function <T = K>(params: Partial<T>) {
      setSearchParams(prev => {
        applyParams(prev, params);
        return prev;
      });
    },
    [setSearchParams],
  );

  // TODO: remover essa função e usar apenas o setFilterParams
  const deleteFilterParams = useCallback(
    function <T = K, P = T>(keys: keyof T | (keyof T)[], params?: Partial<P>) {
      setSearchParams(prev => {
        if (isArray(keys)) {
          keys.map(key => prev.delete(key as string));
        } else {
          prev.delete(keys as string);
        }
        if (params) applyParams(prev, params);
        return prev;
      });
    },
    [setSearchParams],
  );

  const deleteFilterValue = useCallback(
    function <T = K, P = T>(key: keyof T, value?: string, params?: Partial<P>) {
      setSearchParams(prev => {
        const values = prev.getAll(key as string);
        if (values && value) {
          const filteredValues = values.filter(
            filterValue => value !== filterValue,
          );
          prev.delete(key as string);
          filteredValues.forEach(filteredValue =>
            prev.append(key as string, filteredValue),
          );
        }
        if (params) applyParams(prev, params);
        return prev;
      });
    },
    [setSearchParams],
  );

  return {
    filterParams,
    setFilterParams,
    deleteFilterParams,
    deleteFilterValue,
  };
}
